import styled from 'styled-components';
import colors from 'styles/colors';

import { Input as GlobalInput } from 'styles/form';
import { SearchWrapper } from 'components/Search/Search.styles';
import {
  SaveButton as OriginalSaveButton,
  CancelButton as OriginalCancelButton,
  SuccessButton,
  DangerButton,
} from 'components/Buttons';
import media from './media';

export const HEADER_BORDER = `1px solid ${colors.fleetOffGray}`;

export const Container = styled.div<{ menuVisible?: boolean }>`
  z-index: 20;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  ${(props) => props.menuVisible && `margin-top: 50px;`}
`;

const RelativeWrapper = styled.div`
  align-items: center;
  position: fixed;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background-color: ${colors.modalBackground};
  overflow: auto;
`;

export const Relative = (props) => <RelativeWrapper id="relative-modal">{props.children}</RelativeWrapper>;

export const Background = styled.div``;

export const Header = styled.div<{
  justifyContent?: 'space-around' | 'space-between' | 'space-evenly' | 'start' | 'end' | 'center';
  alignItems?: 'start' | 'end' | 'center';
  flexDirection?: 'row' | 'column';
}>`
  min-height: 60px;
  display: flex;
  justify-content: ${(props) => props.justifyContent || 'center'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  position: relative;
  padding: 20px 32px;
  border-bottom: ${HEADER_BORDER};
  flex-direction: ${(props) => props.flexDirection || 'column'};

  ${SearchWrapper} {
    margin-bottom: 32px;
    display: inline-flex;
  }

  i {
    color: ${colors.gray20};
  }
`;

export const Title = styled.h1`
  font-size: 18px;
  line-height: 32px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0;
  padding-right: 24px;
`;

export const CompactTitle = styled.h1`
  font-size: 14px;
  font-weight: 600;
  line-height: 19px;
  margin-top: 20px;
  margin-bottom: 19px;
  text-transform: uppercase;
  text-align: center;
`;

export const Close = styled.i<{ padded?: boolean; disabled?: boolean; isVisible?: boolean }>`
  position: absolute;
  right: ${(props) => (props.padded ? '32px' : '20px')};
  font-size: 15px;
  cursor: ${(props) => (props.disabled ? `not-allowed` : `pointer`)};

  ${(props) =>
    props.disabled
      ? `
        color: ${colors.inactive}
    `
      : null};

  ${(props) => props.isVisible === false && `visibility: hidden`};
`;

export const Content = styled.div`
  padding: 20px 32px;
  font-size: 14px;
  line-height: 20px;
`;

export const CenteredContentMixin = `
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
`;

export const CenteredContent = styled(Content)`
  ${CenteredContentMixin};

  p {
    align-self: flex-start;
  }
`;

export const Label = styled.label`
  font-size: 11px;
  line-height: 15px;
  color: ${colors.gray60};
  margin-bottom: 10px;
  display: block;
  text-transform: uppercase;
`;

export const Input = styled(GlobalInput)`
  width: 100%;
`;

export const Footer = styled.div<{
  justifyContent?: 'space-around' | 'space-between' | 'space-evenly' | 'start' | 'end' | 'center';
}>`
  padding: 20px 32px;
  display: flex;
  justify-content: ${(props) => props.justifyContent ?? 'space-between'};
  border-top: 1px solid ${colors.fleetOffGray};

  button {
    min-width: 110px;
    text-transform: uppercase;
  }
`;

export const CancelButton = styled(OriginalCancelButton)`
  min-width: 100px;
  margin-right: 0px;
  text-transform: uppercase;
`;

export const NextButton = styled(SuccessButton)`
  min-width: 100px;
  text-transform: uppercase;
`;

export const SaveButton = styled(OriginalSaveButton)`
  min-width: 100px;
  text-transform: uppercase;
`;

export const SelectButton = SaveButton;

export const DeleteButton = styled(DangerButton)`
  min-width: 100px;
  text-transform: uppercase;
`;

export const CenteredFooter = styled(Footer)`
  justify-content: space-evenly;
  margin: auto;

  ${CancelButton} {
    margin-right: 0;
  }

  button {
    min-width: 110px;
  }
`;

export const ModalBox = styled.div`
  min-width: 400px;
  background-color: ${colors.white};
  border-radius: 4px;
  overflow: visible;
  transition: 0.15s opacity ease-in-out;
`;

export const CompactModalBox = styled(ModalBox)`
  min-width: 320px;

  ${Header} {
    margin: 0;
  }

  ${CenteredFooter} {
    padding: 20px 70px;
  }
`;

export const Modal = styled(ModalBox)`
  top: -100px;
`;

export const CompactModal = styled(CompactModalBox)`
  top: -100px;
`;

export const ResponsiveCompactModal = styled(CompactModal)<{ height: number; mobile?: boolean }>`
  width: 680px;
  ${(props) => media.lessThan.tablet()`
    width: 100vw;
    overflow: scroll;
    // @ts-expect-error TS(2339): Property 'height' does not exist on type 'ThemedSt... Remove this comment to see the full error message
    height: ${props.height}px;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    padding-bottom: env(safe-area-inset-bottom);

    & > div {
      padding-left: 15px;
      padding-right: 15px;
    }
    `}
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px 20px 32px;
  border-bottom: 1px solid ${colors.fleetOffGray};
`;

export const ModalHeaderTitle = styled.div`
  display: flex;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  align-items: center;
  line-height: 30px;
  gap: 8px;
`;

export const ModalContent = styled.div`
  padding: 16px 32px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ModalFooter = styled.div`
  padding: 20px 32px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid ${colors.fleetOffGray};
`;
